import { ReactNode, useCallback, useEffect, useId, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslate } from 'i18n-calypso';
import clsx from 'clsx';

import AppHeader from './header';
import SideNav, { NavShortcut } from './side-nav';

import './style.scss';

// Matches `$_nav-breakpoint` in style.scss: below it the nav is an off-canvas drawer, not a column.
const NAV_BREAKPOINT = 900;
const WIDE_QUERY = `(min-width: ${ NAV_BREAKPOINT }px)`;
const COLLAPSED_STORAGE_KEY = 'wpcloud-shell-nav-collapsed';
// The WordPress "primary" modifier: Cmd on Apple platforms, Ctrl elsewhere.
const IS_APPLE = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test( navigator.userAgent );

function readCollapsed() {
	try {
		return window.localStorage.getItem( COLLAPSED_STORAGE_KEY ) === '1';
	} catch {
		return false;
	}
}

function useMediaQuery( query: string ) {
	const [ matches, setMatches ] = useState( () => window.matchMedia( query ).matches );
	useEffect( () => {
		const list = window.matchMedia( query );
		const onChange = () => setMatches( list.matches );
		onChange();
		list.addEventListener( 'change', onChange );

		return () => list.removeEventListener( 'change', onChange );
	}, [ query ] );

	return matches;
}

interface Props {
	/** Render the side navigation. Its items come from the current route (see `use-shell-nav`). */
	hasNav?: boolean;
	/** Controls shown above the page content, e.g. the Automattician client picker. */
	contextBar?: ReactNode;
	children: ReactNode;
}

/**
 * The redesigned application frame: top header, optional side navigation, scrolling main column.
 *
 * Consumers keep rendering `<Layout>`; it delegates here while the `wpcloud-redesign` flag is on.
 */
export default function AppShell( { hasNav = false, contextBar = null, children }: Props ) {
	const translate = useTranslate();
	const [ navOpen, setNavOpen ] = useState( false );
	const [ navCollapsed, setNavCollapsed ] = useState( readCollapsed );
	// The rail is a column layout; the narrow drawer always shows labels.
	const isWide = useMediaQuery( WIDE_QUERY );
	const navId = useId();
	const closeNav = useCallback( () => setNavOpen( false ), [] );
	const toggleNav = useCallback( () => setNavOpen( open => ! open ), [] );
	const toggleNavCollapsed = useCallback( () => setNavCollapsed( collapsed => ! collapsed ), [] );

	const navShortcut: NavShortcut = IS_APPLE
		? { display: '⌘B', ariaLabel: String( translate( 'Command B' ) ) }
		: { display: 'Ctrl+B', ariaLabel: String( translate( 'Control B' ) ) };

	// The document is the scroll container, so reset it when the route changes — otherwise a new page
	// opens scrolled to wherever the previous one was left.
	const { pathname } = useLocation();
	useEffect( () => {
		window.scrollTo( { top: 0, left: 0 } );
	}, [ pathname ] );

	// Remember the icon rail between visits. Storage can be unavailable (private windows, blocked site
	// data); the choice then lasts for the page view.
	useEffect( () => {
		try {
			window.localStorage.setItem( COLLAPSED_STORAGE_KEY, navCollapsed ? '1' : '0' );
		} catch {
			// Nothing to persist to.
		}
	}, [ navCollapsed ] );

	// Cmd/Ctrl+B shows or hides the nav: the icon rail on wide viewports, the drawer on narrow ones.
	// Rich-text fields keep the shortcut for bold.
	useEffect( () => {
		if ( ! hasNav ) {
			return;
		}
		const onKeyDown = ( event: KeyboardEvent ) => {
			const primary = IS_APPLE ? event.metaKey && ! event.ctrlKey : event.ctrlKey && ! event.metaKey;
			// `key` is missing on the synthetic keydown Chrome fires for autofill.
			if ( ! primary || event.altKey || event.shiftKey || event.repeat || ( event.key || '' ).toLowerCase() !== 'b' ) {
				return;
			}
			if ( event.target instanceof HTMLElement && event.target.isContentEditable ) {
				return;
			}
			event.preventDefault();
			if ( window.matchMedia( WIDE_QUERY ).matches ) {
				toggleNavCollapsed();
			} else {
				toggleNav();
			}
		};
		document.addEventListener( 'keydown', onKeyDown );

		return () => document.removeEventListener( 'keydown', onKeyDown );
	}, [ hasNav, toggleNav, toggleNavCollapsed ] );

	return (
		<div
			className={ clsx( 'app-shell', {
				'app-shell--no-nav': ! hasNav,
				'app-shell--nav-open': navOpen,
				'app-shell--nav-collapsed': hasNav && navCollapsed,
			} ) }
		>
			<AppHeader hasNav={ hasNav } navOpen={ navOpen } navId={ navId } onToggleNav={ toggleNav } />

			<div className="app-shell__body">
				{ hasNav && (
					<SideNav
						id={ navId }
						open={ navOpen }
						onClose={ closeNav }
						collapsed={ navCollapsed && isWide }
						onToggleCollapsed={ toggleNavCollapsed }
						shortcut={ navShortcut }
					/>
				) }
				{ hasNav && <div className="app-shell__scrim" onClick={ closeNav } aria-hidden="true" /> }

				<div className="app-shell__main">
					{ contextBar && <div className="app-shell__context">{ contextBar }</div> }
					{ children }
				</div>
			</div>
		</div>
	);
}
